import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Platform,
  FlatList,
  Dimensions,
} from 'react-native';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, FormHeader, ConfirmationModal } from '../components';
import { Button } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { getImageUrlSync, getApiBaseURL } from '../utils/imageProxy';
import { getCurrencySymbol } from '../utils/currencies';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { businessCurrency } from '../utils/businessDefaults';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PRODUCT_TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  physical: { label: 'Physical',  icon: 'cube-outline',      color: '#2563eb' },
  digital:  { label: 'Digital',   icon: 'sparkles-outline',  color: '#7c3aed' },
  service:  { label: 'Service',   icon: 'briefcase-outline', color: '#059669' },
};

/** Strips HTML tags for plain-text fallback on native if needed */
const isHtml = (s: string) => /<[a-z][\s\S]*>/i.test(s);

/** Builds a full HTML document for rendering Quill output with correct styles and font */
const buildDescHtml = (html: string) => `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;font-size:15px;color:#111;padding:4px 0 8px;background:transparent;line-height:1.6;word-break:break-word}
/* Quill output classes */
.ql-align-center{text-align:center}
.ql-align-right{text-align:right}
.ql-align-justify{text-align:justify}
.ql-indent-1{padding-left:3em}
.ql-indent-2{padding-left:6em}
.ql-indent-3{padding-left:9em}
p{margin-bottom:6px}
h1{font-size:22px;font-weight:700;margin-bottom:8px;line-height:1.3}
h2{font-size:19px;font-weight:700;margin-bottom:6px;line-height:1.3}
h3{font-size:16px;font-weight:600;margin-bottom:4px;line-height:1.3}
ul,ol{padding-left:1.5em;margin-bottom:8px}
li{margin-bottom:2px}
blockquote{border-left:3px solid #e5e7eb;margin:0 0 8px;padding-left:12px;color:#6b7280}
img{max-width:100%;height:auto;border-radius:6px}
a{color:#7c3aed;text-decoration:underline}
strong{font-weight:700}
em{font-style:italic}
s{text-decoration:line-through}
u{text-decoration:underline}
.ql-size-small{font-size:0.75em}
.ql-size-large{font-size:1.5em}
.ql-size-huge{font-size:2em}
</style></head><body>
<div>${html}</div>
<script>
function sendHeight(){
  var h=document.body.scrollHeight;
  if(window.ReactNativeWebView) window.ReactNativeWebView.postMessage(JSON.stringify({type:'height',value:h}));
  else if(window.parent!==window) window.parent.postMessage({type:'height',value:h},'*');
}
window.addEventListener('load',sendHeight);
new MutationObserver(sendHeight).observe(document.body,{childList:true,subtree:true,attributes:true});
setTimeout(sendHeight,100);
</script>
</body></html>`;

/** Renders Quill HTML output on both web (iframe) and native (WebView), auto-sizing to content */
const DescriptionRenderer: React.FC<{ html: string }> = ({ html }) => {
  const [height, setHeight] = useState(80);
  const srcDoc = buildDescHtml(html);

  const onMsg = (data: string) => {
    try {
      const msg = JSON.parse(data);
      if (msg.type === 'height' && msg.value > 0) setHeight(msg.value + 16);
    } catch {}
  };

  if (Platform.OS === 'web') {
    return (
      <iframe
        // @ts-ignore web only
        srcDoc={srcDoc}
        style={{ width: '100%', height, border: 'none', display: 'block' }}
        sandbox="allow-same-origin allow-scripts"
        onLoad={(e: any) => {
          // Also try reading height from iframe directly
          try {
            const doc = e.target.contentDocument;
            if (doc) setHeight(doc.body.scrollHeight + 16);
          } catch {}
        }}
      />
    );
  }

  return (
    <WebView
      source={{ html: srcDoc }}
      style={{ height, width: '100%', backgroundColor: 'transparent' }}
      scrollEnabled={false}
      javaScriptEnabled
      domStorageEnabled
      originWhitelist={['*']}
      onMessage={(e) => onMsg(e.nativeEvent.data)}
    />
  );
};

interface ProductDetailScreenProps {
  productId: string;
}

export const ProductDetailScreen: React.FC<ProductDetailScreenProps> = ({
  productId,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const returnTo = routeParams?.productReturnTo;
  const backToProducts = () => {
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else {
      navigateTo('products-list', returnTo?.catalogueId || 'default', returnTo?.params);
    }
  };
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [catalogues, setCatalogues] = useState<any[]>([]);
  const [loadingCatalogue, setLoadingCatalogue] = useState(false);
  const imageGalleryRef = useRef<FlatList>(null);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);
  
  const screenWidth = Dimensions.get('window').width;

  // Load API base URL for image proxy
  useEffect(() => {
    const loadApiBaseURL = async () => {
      const baseURL = await getApiBaseURL();
      setApiBaseURL(baseURL);
    };
    loadApiBaseURL();
  }, []);

  // Helper function to get image URL with proxy support
  const getImageUrl = useCallback((url: string): string => {
    return getImageUrlSync(url, apiBaseURL);
  }, [apiBaseURL]);

  useEffect(() => {
    loadProduct();
  }, [productId]);

  useEffect(() => {
    // Reset to first image when product changes
    if (product) {
      setSelectedImageIndex(0);
      imageGalleryRef.current?.scrollToIndex({ index: 0, animated: false });
    }
  }, [product?._id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProductById(productId);
      if (response.statusCode === 1000 && response.data) {
        setProduct(response.data);
        
        // Load catalogue info for every catalogue this product belongs to
        if (response.data.catalogueIds?.length) {
          loadCatalogues(response.data.catalogueIds);
        }
      } else {
        showNotification('error', 'Error', 'Failed to load product');
        setTimeout(() => {
          backToProducts();
        }, 2000);
        return;
      }
    } catch (error: any) {
      console.error('Error loading product:', error);
      showNotification('error', 'Error', error.message || 'Failed to load product');
      setTimeout(() => {
        backToProducts();
      }, 2000);
      return;
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogues = async (catalogueIds: string[]) => {
    try {
      setLoadingCatalogue(true);
      const results = await Promise.all(
        catalogueIds.map((id) => apiService.getCatalogueById(id).catch(() => null))
      );
      setCatalogues(
        results
          .filter((r) => r && r.statusCode === 1000 && r.data)
          .map((r) => r!.data)
      );
    } catch (error: any) {
      console.error('Error loading catalogues:', error);
    } finally {
      setLoadingCatalogue(false);
    }
  };

  const handleEdit = () => {
    navigateTo('product-edit', productId);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const response = await apiService.deleteProduct(productId);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Product deleted successfully.');
        setShowDeleteModal(false);
        setTimeout(() => {
          backToProducts();
        }, 1500);
      } else {
        const errorMessage = response.errors?.[0]?.message || 'Failed to delete product';
        showNotification('error', 'Error', errorMessage);
        setShowDeleteModal(false);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || 'Failed to delete product');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  if (loading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        formHeader={
          <FormHeader
            title="Product Details"
            onBack={() => {
              backToProducts();
            }}
          />
        }
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading product...</Text>
        </View>
      </ScreenLayout>
    );
  }

  if (!product) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        formHeader={
          <FormHeader
            title="Product Details"
            onBack={() => {
              backToProducts();
            }}
          />
        }
      >
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorText}>Product not found</Text>
          <Button
            title="Go Back"
            onPress={() => {
              backToProducts();
            }}
          />
        </View>
      </ScreenLayout>
    );
  }

  const productImages = product.images && product.images.length > 0 
    ? product.images.map((img: any) => img.url).filter(Boolean)
    : product.primaryImage 
      ? [product.primaryImage]
      : [];

  const displayPrice = product.salePrice || product.price;
  const hasDiscount = product.salePrice && product.salePrice < product.price;

  const renderImageItem = ({ item, index }: { item: string; index: number }) => {
    const imageUrl = getImageUrl(item);
    return (
      <View style={[styles.galleryImageContainer, { width: screenWidth }]}>
        <Image 
          source={{ uri: imageUrl }} 
          style={styles.galleryImage} 
          resizeMode="contain"
        />
      </View>
    );
  };

  const renderThumbnail = ({ item, index }: { item: string; index: number }) => {
    const imageUrl = getImageUrl(item);
    return (
      <TouchableOpacity
        style={[
          styles.thumbnail,
          selectedImageIndex === index && styles.thumbnailActive,
        ]}
        onPress={() => {
          setSelectedImageIndex(index);
          imageGalleryRef.current?.scrollToIndex({ index, animated: true });
        }}
      >
        <Image 
          source={{ uri: imageUrl }} 
          style={styles.thumbnailImage} 
          resizeMode="cover"
        />
      </TouchableOpacity>
    );
  };

  const handleScroll = (event: any) => {
    const scrollPosition = event.nativeEvent.contentOffset.x;
    const index = Math.round(scrollPosition / screenWidth);
    if (index >= 0 && index < productImages.length && index !== selectedImageIndex) {
      setSelectedImageIndex(index);
    }
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title="Product Details"
          onBack={() => {
            backToProducts();
          }}
          actions={[
            { icon: 'create-outline', onPress: handleEdit, label: 'Edit', accessibilityLabel: 'Edit product' },
            { icon: 'trash-outline', onPress: handleDelete, label: 'Delete', accessibilityLabel: 'Delete product' },
          ]}
        />
      }
    >
      <ScrollView
        style={styles.container} 
        contentContainerStyle={[
          styles.contentContainer,
          Platform.OS === 'web' && styles.contentContainerWeb,
          Platform.OS !== 'web' && styles.contentContainerMobile
        ]}
        showsVerticalScrollIndicator={false}
      >
        {/* Product Images Gallery */}
        {productImages.length > 0 ? (
          <View style={styles.imageGalleryContainer}>
            <FlatList
              ref={imageGalleryRef}
              data={productImages}
              renderItem={renderImageItem}
              keyExtractor={(item, index) => `image-${index}`}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              onScroll={handleScroll}
              scrollEventThrottle={16}
              onMomentumScrollEnd={(event) => {
                const scrollPosition = event.nativeEvent.contentOffset.x;
                const index = Math.round(scrollPosition / screenWidth);
                if (index >= 0 && index < productImages.length) {
                  setSelectedImageIndex(index);
                }
              }}
              style={styles.imageGallery}
              getItemLayout={(data, index) => ({
                length: screenWidth,
                offset: screenWidth * index,
                index,
              })}
              onScrollToIndexFailed={(info) => {
                // Fallback: wait a bit and try again
                setTimeout(() => {
                  imageGalleryRef.current?.scrollToIndex({ 
                    index: info.index, 
                    animated: false 
                  });
                }, 100);
              }}
            />
            
            {/* Image Counter */}
            {productImages.length > 1 && (
              <View style={styles.imageCounter}>
                <Text style={styles.imageCounterText}>
                  {selectedImageIndex + 1} / {productImages.length}
                </Text>
              </View>
            )}

            {/* Thumbnails */}
            {productImages.length > 1 && (
              <View style={styles.thumbnailsContainer}>
                <FlatList
                  data={productImages}
                  renderItem={renderThumbnail}
                  keyExtractor={(item, index) => `thumb-${index}`}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.thumbnailsList}
                />
              </View>
            )}
          </View>
        ) : (
          <View style={styles.imageContainer}>
            <View style={styles.placeholderImage}>
              <Ionicons name="image-outline" size={64} color={colors.textLight} />
              <Text style={styles.placeholderText}>No images available</Text>
            </View>
          </View>
        )}

        {/* Product Info */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.headerCard}>
            <View style={styles.headerContent}>
              <View style={styles.headerLeft}>
                {/* Product type badge */}
                {(() => {
                  const cfg = PRODUCT_TYPE_CONFIG[product.productType] || PRODUCT_TYPE_CONFIG.physical;
                  return (
                    <View style={[styles.typeBadge, { backgroundColor: cfg.color + '18', borderColor: cfg.color + '40' }]}>
                      <Ionicons name={cfg.icon as any} size={12} color={cfg.color} />
                      <Text style={[styles.typeBadgeText, { color: cfg.color }]}>{cfg.label}</Text>
                    </View>
                  );
                })()}
                {product.brand && (
                  <Text style={styles.brand}>{product.brand}</Text>
                )}
                <Text style={styles.name} numberOfLines={2}>{product.name}</Text>
                {product.sku && (
                  <Text style={styles.sku}>SKU: {product.sku}</Text>
                )}
                {product.barcode && (
                  <Text style={styles.sku}>Barcode: {product.barcode}</Text>
                )}
              </View>
              <View style={styles.headerRight}>
                <TouchableOpacity style={styles.actionButton} onPress={handleEdit}>
                  <Ionicons name="create-outline" size={20} color={colors.primary} />
                </TouchableOpacity>
                <TouchableOpacity style={styles.actionButton} onPress={handleDelete}>
                  <Ionicons name="trash-outline" size={20} color={colors.error} />
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* Price */}
          <View style={styles.priceSection}>
            <View style={styles.priceRow}>
              <Text style={styles.price}>
                {getCurrencySymbol(product.currency || businessCurrency())} {displayPrice?.toFixed(2)}
                {product.priceUnit ? <Text style={styles.priceUnit}> / {product.priceUnit}</Text> : null}
              </Text>
              {hasDiscount && (
                <Text style={styles.originalPrice}>
                  {getCurrencySymbol(product.currency || businessCurrency())} {product.price.toFixed(2)}
                </Text>
              )}
              {hasDiscount && (
                <View style={styles.discountBadge}>
                  <Text style={styles.discountBadgeText}>
                    {Math.round(((product.price - product.salePrice!) / product.price) * 100)}% OFF
                  </Text>
                </View>
              )}
            </View>
            {product.costPrice !== undefined && (
              <Text style={styles.costPrice}>
                Cost: {getCurrencySymbol(product.currency || businessCurrency())} {product.costPrice.toFixed(2)}
              </Text>
            )}
            {product.salePrice !== undefined && !hasDiscount && (
              <Text style={styles.salePriceNote}>
                Sale price: {getCurrencySymbol(product.currency || businessCurrency())} {product.salePrice.toFixed(2)}
              </Text>
            )}
          </View>

          {/* Description */}
          {product.description && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Description</Text>
              {isHtml(product.description) ? (
                <DescriptionRenderer html={product.description} />
              ) : (
                <Text style={styles.description}>{product.description}</Text>
              )}
            </View>
          )}

          {product.customFields && Object.keys(product.customFields).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Custom fields</Text>
              {Object.entries(product.customFields).map(([key, value]) => (
                <View key={key} style={{ paddingVertical: spacing.sm }}>
                  <Text style={{ color: colors.textLight, fontSize: typography.fontSize.sm }}>{key.replace(/_/g, ' ')}</Text>
                  <Text selectable style={styles.description}>
                    {value == null ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Catalogue Info — a product may belong to multiple catalogues */}
          {catalogues.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{catalogues.length > 1 ? 'Catalogues' : 'Catalogue'}</Text>
              {catalogues.map((cat) => (
                <TouchableOpacity
                  key={cat._id || cat.id}
                  style={styles.catalogueCard}
                  onPress={() => navigateTo('catalogue-detail', cat._id || cat.id)}
                  activeOpacity={0.7}
                >
                  <View style={styles.catalogueInfo}>
                    <Ionicons name="cube" size={20} color={colors.primary} />
                    <View style={styles.catalogueDetails}>
                      <Text style={styles.catalogueName}>{cat.name}</Text>
                      {cat.description && (
                        <Text style={styles.catalogueDescription} numberOfLines={1}>
                          {cat.description}
                        </Text>
                      )}
                    </View>
                    <DirectionalIcon name="chevron-forward" size={20} color={colors.textLight} />
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* Inventory / Capacity */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {product.productType === 'digital' ? 'Access / Seats' : product.productType === 'service' ? 'Booking Slots' : 'Inventory'}
            </Text>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Availability:</Text>
              <Text style={styles.infoValue}>
                {product.availability?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'In Stock'}
              </Text>
            </View>
            {product.inventoryQuantity !== undefined && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>
                  {product.productType === 'digital' ? 'Available Seats' : product.productType === 'service' ? 'Available Slots' : 'Quantity'}:
                </Text>
                <Text style={styles.infoValue}>{product.inventoryQuantity}</Text>
              </View>
            )}
            {product.lowStockThreshold !== undefined && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Low Stock Alert:</Text>
                <Text style={styles.infoValue}>{product.lowStockThreshold}</Text>
              </View>
            )}
            {!!(product as any).minimumOrderQuantity && (product as any).minimumOrderQuantity > 1 && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Minimum Order Qty:</Text>
                <Text style={styles.infoValue}>{(product as any).minimumOrderQuantity}</Text>
              </View>
            )}
            {product.condition && product.productType === 'physical' && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Condition:</Text>
                <Text style={styles.infoValue}>{product.condition.charAt(0).toUpperCase() + product.condition.slice(1)}</Text>
              </View>
            )}
            {product.weight !== undefined && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Weight:</Text>
                <Text style={styles.infoValue}>{product.weight} kg</Text>
              </View>
            )}
            {product.dimensions && (product.dimensions.length || product.dimensions.width || product.dimensions.height) && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Dimensions:</Text>
                <Text style={styles.infoValue}>
                  {[product.dimensions.length, product.dimensions.width, product.dimensions.height].filter(Boolean).join(' × ')} cm
                </Text>
              </View>
            )}
          </View>

          {/* Digital delivery */}
          {product.productType === 'digital' && product.digitalDelivery && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Digital Delivery</Text>
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>Method:</Text>
                <Text style={styles.infoValue}>{product.digitalDelivery.charAt(0).toUpperCase() + product.digitalDelivery.slice(1)}</Text>
              </View>
            </View>
          )}

          {/* Service delivery */}
          {product.productType === 'service' && (product.serviceDelivery || product.serviceLocation) && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Service Details</Text>
              {product.serviceDelivery && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Delivery:</Text>
                  <Text style={styles.infoValue}>{product.serviceDelivery.charAt(0).toUpperCase() + product.serviceDelivery.slice(1).replace('_', ' ')}</Text>
                </View>
              )}
              {product.serviceLocation && (
                <View style={styles.infoRow}>
                  <Text style={styles.infoLabel}>Location:</Text>
                  <Text style={styles.infoValue}>{product.serviceLocation}</Text>
                </View>
              )}
            </View>
          )}

          {/* Addons */}
          {product.addons && product.addons.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Add-ons</Text>
              {product.addons.map((addon: any, index: number) => (
                <View key={addon.id || index} style={styles.addonCard}>
                  <View style={styles.addonRow}>
                    <View style={styles.addonLeft}>
                      <Text style={styles.addonName}>{addon.name}</Text>
                      {addon.description ? (
                        <Text style={styles.addonDesc}>{addon.description}</Text>
                      ) : null}
                      <View style={styles.addonMeta}>
                        {addon.isRequired && (
                          <View style={styles.addonBadge}>
                            <Text style={styles.addonBadgeText}>Required</Text>
                          </View>
                        )}
                        {addon.maxQuantity && (
                          <Text style={styles.addonMetaText}>Max {addon.maxQuantity}</Text>
                        )}
                      </View>
                    </View>
                    <Text style={styles.addonPrice}>
                      +{getCurrencySymbol(product.currency || businessCurrency())}{addon.price.toFixed(2)}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Variants */}
          {product.hasVariants && product.variants && product.variants.length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Variants</Text>
              {product.variants.map((variant: any, index: number) => (
                <View key={variant.id || index} style={styles.variantCard}>
                  <Text style={styles.variantName}>{variant.name}</Text>
                  {variant.price !== undefined && (
                    <Text style={styles.variantPrice}>
                      {getCurrencySymbol(product.currency || businessCurrency())} {variant.price.toFixed(2)}
                    </Text>
                  )}
                </View>
              ))}
            </View>
          )}

        </View>
      </ScrollView>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Product"
        message="Are you sure you want to delete this product? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
        loading={deleting}
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },
  contentContainerMobile: {
    paddingBottom: 100, // Extra padding for mobile to avoid bottom tabs overlay
  },
  contentContainerWeb: {
    paddingBottom: spacing.sm, // Minimal padding on web (ScreenLayout handles spacing)
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  errorText: {
    fontSize: typography.fontSize.lg,
    color: colors.error,
    marginTop: spacing.md,
    marginBottom: spacing.lg,
    fontFamily: typography.fontFamily.semibold,
  },
  imageGalleryContainer: {
    width: '100%',
    marginBottom: spacing.md,
    backgroundColor: colors.background.surface,
  },
  imageGallery: {
    width: '100%',
    height: 300,
  },
  galleryImageContainer: {
    height: 300,
    backgroundColor: colors.background.surface,
    justifyContent: 'center',
    alignItems: 'center',
  },
  galleryImage: {
    width: '100%',
    height: '100%',
  },
  imageContainer: {
    width: '100%',
    height: 300,
    backgroundColor: colors.background.surface,
    marginBottom: spacing.md,
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
  },
  placeholderText: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  imageCounter: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.sm,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
  },
  imageCounterText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  thumbnailsContainer: {
    width: '100%',
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.sm,
  },
  thumbnailsList: {
    paddingHorizontal: spacing.sm,
    gap: spacing.xs,
  },
  thumbnail: {
    width: 60,
    height: 60,
    borderRadius: designTokens.borderRadius.sm,
    borderWidth: 2,
    borderColor: colors.border,
    overflow: 'hidden',
    marginRight: spacing.xs,
  },
  thumbnailActive: {
    borderColor: colors.primary,
    borderWidth: 3,
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
  },
  content: {
    padding: spacing.md,
  },
  headerCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  headerLeft: {
    flex: 1,
    minWidth: 0,
    marginRight: spacing.sm,
  },
  typeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: spacing.xs,
  },
  typeBadgeText: {
    fontSize: 11,
    fontFamily: typography.fontFamily.medium,
    fontWeight: '600',
  },
  brand: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.primary,
  },
  name: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.semibold,
  },
  sku: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  headerRight: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  priceSection: {
    backgroundColor: colors.background.surface,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    marginBottom: spacing.md,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  price: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: 'bold',
    color: colors.primary,
    fontFamily: typography.fontFamily.bold,
  },
  priceUnit: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    fontWeight: 'normal',
  },
  originalPrice: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textDecorationLine: 'line-through',
    fontFamily: typography.fontFamily.primary,
  },
  discountBadge: {
    backgroundColor: colors.error + '18',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  discountBadgeText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600',
  },
  costPrice: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs,
  },
  salePriceNote: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.xs,
  },
  section: {
    backgroundColor: colors.background.surface,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.semibold,
  },
  description: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    lineHeight: 24,
    fontFamily: typography.fontFamily.primary,
  },
  catalogueCard: {
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  catalogueInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  catalogueDetails: {
    flex: 1,
    minWidth: 0,
  },
  catalogueName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: spacing.xs / 2,
  },
  catalogueDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  infoLabel: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  infoValue: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  addonCard: {
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  addonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  addonLeft: { flex: 1, marginRight: spacing.sm },
  addonName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    fontWeight: '600',
  },
  addonDesc: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginTop: 2,
  },
  addonMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  addonBadge: {
    backgroundColor: colors.primary + '18',
    borderRadius: 10,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  addonBadgeText: {
    fontSize: 11,
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
    fontWeight: '600',
  },
  addonMetaText: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  addonPrice: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
    fontWeight: '600',
  },
  variantCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: spacing.sm,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.sm,
    marginBottom: spacing.xs,
  },
  variantName: {
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
  variantPrice: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.primary,
    fontFamily: typography.fontFamily.semibold,
  },
  actions: {
    marginTop: spacing.md,
    marginBottom: spacing.xl,
  },
  actionButtonFull: {
    width: '100%',
  },
});

